import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Wrench, Search, Copy, Check, RefreshCw, AlertTriangle, CheckCircle2, FlaskConical, ShieldCheck, Database } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { ScheduledJobsTab } from '@/pages/settings/ScheduledJobsTab.tsx'

// ─── Types ──────────────────────────────────────────────────────────────────

interface TanssRawResponse {
  status: number
  data: unknown
  error?: boolean
}

interface SyncMapRow {
  id: string
  userId: string
  m365EventId: string | null
  tanssSupportId: string | null
  tanssPlanningType: string | null
  origin: 'outlook' | 'tanss'
  lastM365Hash: string | null
  lastTanssHash: string | null
  lastSyncedAt: string | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

interface SyncMapResponse {
  userId: string
  total: number
  includeDeleted: boolean
  rows: SyncMapRow[]
}

interface JobHistoryRow {
  id: number
  jobKey: string
  startedAt: string
  finishedAt: string
  status: string
  durationMs: number
  message: string | null
  error: string | null
}

interface JobHistoryResponse {
  total: number
  jobs: Array<{ key: string; label: string }>
  history: JobHistoryRow[]
}

interface SelfTestResponse {
  ok: boolean
  checks: Array<{ name: string; ok: boolean; detail?: string }>
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractErrorMessage(err: unknown): string {
  const ax = err as { response?: { data?: unknown }; message?: string }
  const data = ax?.response?.data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
  }
  return ax?.message ?? 'Unbekannter Fehler'
}

function JsonViewer({ data }: { data: unknown }) {
  const [copied, setCopied] = useState(false)
  const json = JSON.stringify(data, null, 2)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="xs"
        leadingIcon={copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        onClick={copy}
        className="absolute right-2 top-2"
      >
        {copied ? 'Kopiert' : 'Kopieren'}
      </Button>
      <pre className="overflow-auto max-h-[480px] rounded border border-gray-200 bg-gray-50 p-3 text-xs font-mono text-gray-800 whitespace-pre">
        {json}
      </pre>
    </div>
  )
}

// ─── TANSS Raw-API-Viewer Tab ──────────────────────────────────────────────

type TanssResource = 'tickets' | 'supports' | 'companies'

function TanssApiViewerTab() {
  const [resource, setResource] = useState<TanssResource>('supports')
  const [id, setId] = useState('')
  const [submitted, setSubmitted] = useState<{ resource: TanssResource; id: string } | null>(null)

  const query = useQuery<TanssRawResponse>({
    queryKey: ['dev-tools', 'tanss', submitted?.resource, submitted?.id],
    queryFn: async () => {
      const { data } = await apiClient.get<TanssRawResponse>(
        `/dev-tools/tanss/${submitted!.resource}/${submitted!.id}`,
      )
      return data
    },
    enabled: Boolean(submitted),
    retry: false,
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = id.trim()
    if (!trimmed) return
    setSubmitted({ resource, id: trimmed })
  }

  return (
    <Card>
      <Card.Header>
        <h3 className="text-h3 font-semibold">TANSS Raw-API-Viewer</h3>
        <p className="text-caption text-gray-500 mt-1">
          Read-only Proxy: GET-Request gegen TANSS, Response 1:1 als JSON. Nutzt deine eigenen TANSS-Credentials.
        </p>
      </Card.Header>
      <Card.Body className="space-y-4">
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="text-caption text-gray-600 block mb-1">Ressource</label>
            <select
              value={resource}
              onChange={(e) => setResource(e.target.value as TanssResource)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-body focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
            >
              <option value="supports">Support</option>
              <option value="tickets">Ticket</option>
              <option value="companies">Company</option>
            </select>
          </div>
          <div className="flex-1">
            <Input
              label="ID"
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="z. B. 287423"
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            leadingIcon={<Search className="w-4 h-4" />}
            disabled={!id.trim()}
            loading={query.isFetching}
          >
            Abfragen
          </Button>
        </form>

        {submitted && query.isLoading && <LoadingState />}
        {submitted && query.isError && (
          <ErrorState
            title="Abruf fehlgeschlagen"
            details={extractErrorMessage(query.error)}
            onRetry={() => query.refetch()}
          />
        )}
        {submitted && query.data && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-caption">
              <span className="text-gray-500">Endpoint:</span>
              <code className="px-2 py-0.5 bg-gray-100 rounded">
                GET /api/v1/{submitted.resource}/{submitted.id}
              </code>
              <span className="text-gray-500 ml-2">HTTP-Status:</span>
              <StatusBadge status={query.data.error ? 'error' : 'ok'} label={String(query.data.status)} />
            </div>
            <JsonViewer data={query.data.data} />
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Sync-Map-Viewer Tab ───────────────────────────────────────────────────

function SyncMapTab() {
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const query = useQuery<SyncMapResponse>({
    queryKey: ['dev-tools', 'sync-map', includeDeleted],
    queryFn: async () => {
      const { data } = await apiClient.get<SyncMapResponse>('/dev-tools/sync-map', {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' },
      })
      return data
    },
  })

  return (
    <Card>
      <Card.Header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-h3 font-semibold">Calendar-Sync-Map</h3>
          <p className="text-caption text-gray-500 mt-1">
            Welche Outlook-Events sind mit welchen TANSS-Supports verheiratet — sortiert nach letztem Sync.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-caption text-gray-600 flex items-center gap-1">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            inkl. geloeschter
          </label>
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<RefreshCw className="w-3 h-3" />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            Aktualisieren
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {query.isLoading && <LoadingState />}
        {query.isError && (
          <ErrorState
            title="Sync-Map konnte nicht geladen werden"
            details={extractErrorMessage(query.error)}
            onRetry={() => query.refetch()}
          />
        )}
        {query.data && query.data.rows.length === 0 && (
          <EmptyState
            size="sm"
            title="Keine Map-Eintraege"
            description="Der Calendar-Sync hat fuer dich noch keine Outlook↔TANSS-Verbindung hinterlegt."
          />
        )}
        {query.data && query.data.rows.length > 0 && (
          <>
            <div className="text-caption text-gray-500 mb-3">
              {query.data.total} Eintraege fuer User <code>{query.data.userId}</code>
            </div>
            <DataTable<SyncMapRow>
              data={query.data.rows}
              getRowId={(r) => r.id}
              columns={[
                {
                  accessorKey: 'origin',
                  header: 'Origin',
                  cell: ({ row }) => (
                    <StatusBadge
                      status={row.original.origin === 'outlook' ? 'info' : 'success'}
                      label={row.original.origin}
                    />
                  ),
                },
                {
                  accessorKey: 'tanssSupportId',
                  header: 'TANSS-Support',
                  cell: ({ row }) => (
                    <code className="text-caption">{row.original.tanssSupportId ?? '—'}</code>
                  ),
                },
                {
                  accessorKey: 'tanssPlanningType',
                  header: 'PlanningType',
                  cell: ({ row }) => (
                    <span className="text-caption">{row.original.tanssPlanningType ?? '—'}</span>
                  ),
                },
                {
                  accessorKey: 'm365EventId',
                  header: 'Outlook-Event',
                  cell: ({ row }) => (
                    <code
                      className="text-caption block max-w-[280px] truncate"
                      title={row.original.m365EventId ?? ''}
                    >
                      {row.original.m365EventId ?? '—'}
                    </code>
                  ),
                },
                {
                  accessorKey: 'lastSyncedAt',
                  header: 'Letzter Sync',
                  cell: ({ row }) =>
                    row.original.lastSyncedAt ? (
                      <span className="text-caption">
                        {new Date(row.original.lastSyncedAt).toLocaleString('de-DE')}
                      </span>
                    ) : (
                      <span className="text-caption text-gray-400">—</span>
                    ),
                },
                {
                  accessorKey: 'deletedAt',
                  header: 'Status',
                  cell: ({ row }) =>
                    row.original.deletedAt ? (
                      <StatusBadge status="error" label="geloescht" />
                    ) : (
                      <StatusBadge status="success" label="aktiv" />
                    ),
                },
              ]}
            />
          </>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Job-History Tab ───────────────────────────────────────────────────────

function JobHistoryTab() {
  const [jobKey, setJobKey] = useState('')
  const query = useQuery<JobHistoryResponse>({
    queryKey: ['dev-tools', 'job-history', jobKey],
    queryFn: async () => {
      const { data } = await apiClient.get<JobHistoryResponse>('/dev-tools/job-history', {
        params: jobKey ? { jobKey, limit: 200 } : { limit: 200 },
      })
      return data
    },
  })

  return (
    <Card>
      <Card.Header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-h3 font-semibold">Job-History</h3>
          <p className="text-caption text-gray-500 mt-1">
            Letzte 200 Cron-Job-Laeufe (FIFO, max. 500 pro Job in der DB).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={jobKey}
            onChange={(e) => setJobKey(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-body focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
          >
            <option value="">Alle Jobs</option>
            {query.data?.jobs.map((j) => (
              <option key={j.key} value={j.key}>
                {j.label}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<RefreshCw className="w-3 h-3" />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            Aktualisieren
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {query.isLoading && <LoadingState />}
        {query.isError && (
          <ErrorState
            title="Job-History konnte nicht geladen werden"
            details={extractErrorMessage(query.error)}
            onRetry={() => query.refetch()}
          />
        )}
        {query.data && query.data.history.length === 0 && (
          <EmptyState
            size="sm"
            title="Keine Job-Laeufe"
            description="Es wurden noch keine Cron-Job-Laeufe protokolliert."
          />
        )}
        {query.data && query.data.history.length > 0 && (
          <DataTable<JobHistoryRow>
            data={query.data.history}
            getRowId={(r) => String(r.id)}
            columns={[
              {
                accessorKey: 'startedAt',
                header: 'Start',
                cell: ({ row }) => (
                  <span className="text-caption">
                    {new Date(row.original.startedAt).toLocaleString('de-DE')}
                  </span>
                ),
              },
              {
                accessorKey: 'jobKey',
                header: 'Job',
                cell: ({ row }) => (
                  <code className="text-caption">{row.original.jobKey}</code>
                ),
              },
              {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => (
                  <StatusBadge
                    status={row.original.status === 'success' ? 'success' : 'error'}
                    label={row.original.status}
                  />
                ),
              },
              {
                accessorKey: 'durationMs',
                header: 'Dauer',
                cell: ({ row }) => (
                  <span className="text-caption">{(row.original.durationMs / 1000).toFixed(2)}s</span>
                ),
              },
              {
                accessorKey: 'message',
                header: 'Message / Error',
                cell: ({ row }) => {
                  const r = row.original
                  return (
                    <div className="text-caption max-w-[500px]">
                      {r.error ? (
                        <span className="text-red-700">{r.error}</span>
                      ) : (
                        <span>{r.message ?? '—'}</span>
                      )}
                    </div>
                  )
                },
              },
            ]}
          />
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Self-Test Tab ─────────────────────────────────────────────────────────

function SelfTestTab() {
  const mut = useMutation<SelfTestResponse>({
    mutationFn: async () => {
      const { data } = await apiClient.get<SelfTestResponse>('/dev-tools/self-test')
      return data
    },
  })

  return (
    <Card>
      <Card.Header>
        <h3 className="text-h3 font-semibold">Self-Test</h3>
        <p className="text-caption text-gray-500 mt-1">
          Schnelle Diagnose: Sind M365-Token, TANSS-Login und Employee-ID OK?
        </p>
      </Card.Header>
      <Card.Body className="space-y-3">
        <Button
          variant="primary"
          leadingIcon={<RefreshCw className="w-4 h-4" />}
          onClick={() => mut.mutate()}
          loading={mut.isPending}
        >
          Self-Test ausfuehren
        </Button>

        {mut.data && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {mut.data.ok ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              )}
              <span className="text-body font-medium">
                {mut.data.ok ? 'Alle Checks bestanden' : 'Es gibt Probleme'}
              </span>
            </div>
            <ul className="space-y-1">
              {mut.data.checks.map((c, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-small px-3 py-2 rounded border border-gray-200 bg-gray-50"
                >
                  {c.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <div className={c.ok ? 'text-gray-900' : 'text-red-800 font-medium'}>{c.name}</div>
                    {c.detail && <div className="text-caption text-gray-600">{c.detail}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {mut.error && (
          <ErrorState
            title="Self-Test fehlgeschlagen"
            details={extractErrorMessage(mut.error)}
          />
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Adapter-Tests Tab ────────────────────────────────────────────────────

interface AdapterTestResult {
  id: string
  label: string
  configured: boolean
  reachable: boolean | null
  detail: string
  durationMs: number
}

function AdapterStatus({ result }: { result: AdapterTestResult }) {
  if (!result.configured) {
    return <StatusBadge status="inactive" label="Nicht konfiguriert" />
  }
  if (result.reachable === null) {
    return <StatusBadge status="pending" label="Konfiguriert" />
  }
  return <StatusBadge status={result.reachable ? 'active' : 'error'} label={result.reachable ? 'Erreichbar' : 'Fehler'} />
}

function AdapterTestsTab() {
  const query = useQuery<{ results: AdapterTestResult[] }>({
    queryKey: ['dev-tools', 'adapter-tests'],
    queryFn: async () => {
      const { data } = await apiClient.get('/dev-tools/adapter-tests')
      return data
    },
    staleTime: 30_000,
  })

  return (
    <Card>
      <Card.Header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-h3 font-semibold flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-brand-500" />
            Adapter-Tests
          </h3>
          <p className="text-caption text-gray-500 mt-1">
            Verbindung zu externen Systemen prüfen — TANSS, SAP, M365, EGIS, ALSO.
          </p>
        </div>
        <Button
          variant="ghost"
          size="xs"
          leadingIcon={<RefreshCw className="w-3 h-3" />}
          onClick={() => query.refetch()}
          loading={query.isFetching}
        >
          Alle testen
        </Button>
      </Card.Header>
      <Card.Body>
        {query.isLoading && <LoadingState />}
        {query.isError && (
          <ErrorState
            title="Tests konnten nicht ausgeführt werden"
            details={extractErrorMessage(query.error)}
            onRetry={() => query.refetch()}
          />
        )}
        {query.data && (
          <div className="space-y-2">
            {query.data.results.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{r.label}</div>
                  <div className="text-caption text-gray-500 mt-0.5 truncate">{r.detail}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-caption text-gray-400">{r.durationMs}ms</span>
                  <AdapterStatus result={r} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Audit-Logs Tab ────────────────────────────────────────────────────────

interface AuditLogRow {
  id: number
  userId: string | null
  userName: string | null
  action: string
  resource: string
  resourceId: string | null
  details: Record<string, unknown> | null
  ipAddress: string | null
  createdAt: string
}

interface AuditLogsResponse {
  data: AuditLogRow[]
  total: number
  page: number
  pageSize: number
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Erstellt',
  update: 'Aktualisiert',
  delete: 'Gelöscht',
  login: 'Login',
  logout: 'Logout',
  export: 'Export',
  send: 'Gesendet',
  sync: 'Sync',
}

function AuditLogsTab() {
  const [page, setPage] = useState(1)
  const [actionFilter, setActionFilter] = useState('')
  const [resourceFilter, setResourceFilter] = useState('')

  const query = useQuery<AuditLogsResponse>({
    queryKey: ['audit-logs', page, actionFilter, resourceFilter],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, pageSize: 50 }
      if (actionFilter) params['action'] = actionFilter
      if (resourceFilter) params['resource'] = resourceFilter
      const { data } = await apiClient.get('/audit', { params })
      return data
    },
  })

  return (
    <Card>
      <Card.Header>
        <h3 className="text-h3 font-semibold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand-500" />
          Audit-Logs
        </h3>
        <p className="text-caption text-gray-500 mt-1">
          Alle protokollierten Aktionen aller Nutzer im System.
        </p>
      </Card.Header>
      <Card.Body className="space-y-4">
        <FilterBar>
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
          >
            <option value="">Alle Aktionen</option>
            {Object.entries(ACTION_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <Input
            placeholder="Ressource filtern…"
            value={resourceFilter}
            onChange={(e) => { setResourceFilter(e.target.value); setPage(1) }}
            className="w-48"
          />
          {(actionFilter || resourceFilter) && (
            <FilterChip
              active
              onRemove={() => { setActionFilter(''); setResourceFilter(''); setPage(1) }}
            >
              Filter zurücksetzen
            </FilterChip>
          )}
          <Button
            variant="ghost"
            size="xs"
            leadingIcon={<RefreshCw className="w-3 h-3" />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            Aktualisieren
          </Button>
        </FilterBar>

        {query.isLoading && <LoadingState />}
        {query.isError && (
          <ErrorState
            title="Audit-Logs konnten nicht geladen werden"
            details={extractErrorMessage(query.error)}
            onRetry={() => query.refetch()}
          />
        )}
        {query.data && query.data.data.length === 0 && (
          <EmptyState size="sm" title="Keine Einträge" description="Für die gewählten Filter wurden keine Audit-Logs gefunden." />
        )}
        {query.data && query.data.data.length > 0 && (
          <>
            <DataTable<AuditLogRow>
              data={query.data.data}
              getRowId={(r) => String(r.id)}
              columns={[
                {
                  accessorKey: 'createdAt',
                  header: 'Zeitpunkt',
                  cell: ({ row }) => (
                    <span className="text-caption whitespace-nowrap">
                      {new Date(row.original.createdAt).toLocaleString('de-DE')}
                    </span>
                  ),
                },
                {
                  accessorKey: 'userName',
                  header: 'Nutzer',
                  cell: ({ row }) => (
                    <span className="text-caption">{row.original.userName ?? row.original.userId ?? 'System'}</span>
                  ),
                },
                {
                  accessorKey: 'action',
                  header: 'Aktion',
                  cell: ({ row }) => (
                    <StatusBadge
                      status={row.original.action === 'delete' ? 'error' : row.original.action === 'create' ? 'success' : 'info'}
                      label={ACTION_LABELS[row.original.action] ?? row.original.action}
                    />
                  ),
                },
                {
                  accessorKey: 'resource',
                  header: 'Ressource',
                  cell: ({ row }) => (
                    <span className="text-caption">
                      <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">{row.original.resource}</code>
                      {row.original.resourceId && (
                        <span className="text-gray-400 ml-1">#{row.original.resourceId}</span>
                      )}
                    </span>
                  ),
                },
                {
                  accessorKey: 'ipAddress',
                  header: 'IP',
                  cell: ({ row }) => (
                    <span className="text-caption text-gray-400">{row.original.ipAddress ?? '—'}</span>
                  ),
                },
              ]}
            />
            <div className="flex items-center justify-between pt-2">
              <span className="text-caption text-gray-500">
                {query.data.total} Einträge gesamt · Seite {query.data.page} / {Math.ceil(query.data.total / query.data.pageSize) || 1}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Zurück
                </Button>
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(query.data.total / query.data.pageSize)}
                >
                  Weiter
                </Button>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── SAP-Dokument-Cache Tab ───────────────────────────────────────────────

interface SapDocCacheStat {
  docType: string
  total: number
  closed: number
  lastSyncedAt: string | null
}

interface SapDocCacheRow {
  id: string
  tenantId: string
  docType: string
  sapDocEntry: number
  sapDocNum: number | null
  cardCode: string | null
  cardName: string | null
  docDate: string | null
  docDueDate: string | null
  docTotal: number | null
  docCurrency: string | null
  docStatus: string | null
  isClosed: boolean
  lastSyncedAt: string | null
  createdAt: string
  updatedAt: string
}

interface SapDocCacheResponse {
  stats: SapDocCacheStat[]
  items: SapDocCacheRow[]
  total: number
  page: number
  pageSize: number
}

const DOC_TYPE_LABELS: Record<string, string> = {
  quotation:             'Angebot',
  order:                 'Auftrag',
  delivery:              'Lieferschein',
  invoice:               'Ausgangsrechnung',
  credit:                'Ausgangsgutschrift',
  purchase_invoice:      'Eingangsrechnung',
  purchase_credit:       'Eingangsgutschrift',
  purchase_down_payment: 'Eingangs-Anzahlung',
}

function fmt(val: string | null | undefined): string {
  if (!val) return '—'
  const d = new Date(val)
  if (isNaN(d.getTime())) return val
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}

function SapDocCacheTab() {
  const [page, setPage] = useState(1)
  const [docTypeFilter, setDocTypeFilter] = useState('')
  const [search, setSearch] = useState('')

  const query = useQuery<SapDocCacheResponse>({
    queryKey: ['sap-doc-cache', page, docTypeFilter, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, pageSize: 50 }
      if (docTypeFilter) params['docType'] = docTypeFilter
      if (search) params['search'] = search
      const { data } = await apiClient.get('/dev-tools/sap-doc-cache', { params })
      return data
    },
  })

  const totalOpen = query.data?.stats.reduce((s, r) => s + r.total - r.closed, 0) ?? 0
  const totalAll  = query.data?.stats.reduce((s, r) => s + r.total, 0) ?? 0
  const lastSync  = query.data?.stats
    .map((r) => r.lastSyncedAt)
    .filter(Boolean)
    .sort()
    .at(-1) ?? null

  return (
    <div className="space-y-4">
      {/* Stats-Uebersicht */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {query.data?.stats.map((s) => (
          <Card key={s.docType}>
            <Card.Body className="py-3 px-4">
              <p className="text-caption text-gray-500">{DOC_TYPE_LABELS[s.docType] ?? s.docType}</p>
              <p className="text-h3 font-semibold mt-0.5">{s.total.toLocaleString('de-DE')}</p>
              <p className="text-caption text-gray-400 mt-0.5">
                {(s.total - s.closed).toLocaleString('de-DE')} offen · {s.closed.toLocaleString('de-DE')} geschlossen
              </p>
              <p className="text-caption text-gray-400 mt-1 truncate" title={s.lastSyncedAt ?? ''}>
                Sync: {fmt(s.lastSyncedAt)}
              </p>
            </Card.Body>
          </Card>
        ))}
        {query.isLoading && (
          <div className="col-span-4"><LoadingState label="Lade Statistiken…" /></div>
        )}
      </div>

      {/* Zusammenfassung + letzter Sync */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h3 className="text-h3 font-semibold flex items-center gap-2">
              <Database className="w-4 h-4 text-brand-500" />
              SAP-Dokument-Cache
            </h3>
            <div className="flex items-center gap-4 text-caption text-gray-500">
              <span>{totalOpen.toLocaleString('de-DE')} offen · {totalAll.toLocaleString('de-DE')} gesamt</span>
              {lastSync && <span>Letzter Sync: {fmt(lastSync)}</span>}
              <Button
                variant="ghost"
                size="xs"
                leadingIcon={<RefreshCw className="w-3 h-3" />}
                onClick={() => query.refetch()}
              >
                Aktualisieren
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="space-y-4">
          <FilterBar>
            <select
              value={docTypeFilter}
              onChange={(e) => { setDocTypeFilter(e.target.value); setPage(1) }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
            >
              <option value="">Alle Typen</option>
              {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <Input
              placeholder="Name, Nummer, CardCode…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-56"
            />
            {(docTypeFilter || search) && (
              <FilterChip active onRemove={() => { setDocTypeFilter(''); setSearch(''); setPage(1) }}>
                Filter zurücksetzen
              </FilterChip>
            )}
          </FilterBar>

          {query.isError && (
            <ErrorState
              title="Fehler beim Laden"
              description={extractErrorMessage(query.error)}
            />
          )}

          {query.isSuccess && (
            <DataTable<SapDocCacheRow>
              data={query.data.items}
              columns={[
                {
                  id: 'docType',
                  header: 'Typ',
                  cell: ({ row }) => (
                    <span className="font-mono text-xs px-1.5 py-0.5 bg-gray-100 rounded">
                      {DOC_TYPE_LABELS[row.original.docType] ?? row.original.docType}
                    </span>
                  ),
                },
                {
                  id: 'sapDocNum',
                  header: 'Beleg-Nr.',
                  cell: ({ row }) => row.original.sapDocNum ?? row.original.sapDocEntry,
                },
                {
                  id: 'cardName',
                  header: 'Partner',
                  cell: ({ row }) => (
                    <span title={row.original.cardCode ?? ''}>
                      {row.original.cardName ?? row.original.cardCode ?? '—'}
                    </span>
                  ),
                },
                {
                  id: 'docDate',
                  header: 'Datum',
                  cell: ({ row }) => row.original.docDate ?? '—',
                },
                {
                  id: 'docTotal',
                  header: 'Betrag',
                  cell: ({ row }) => row.original.docTotal != null
                    ? `${row.original.docTotal.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${row.original.docCurrency ?? ''}`
                    : '—',
                },
                {
                  id: 'docStatus',
                  header: 'Status',
                  cell: ({ row }) => (
                    <StatusBadge status={row.original.isClosed ? 'closed' : 'open'} />
                  ),
                },
                {
                  id: 'lastSyncedAt',
                  header: 'Letzter Sync',
                  cell: ({ row }) => (
                    <span className="text-caption text-gray-500">{fmt(row.original.lastSyncedAt)}</span>
                  ),
                },
              ]}
              pagination={{ pageSize: query.data.pageSize }}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────

// ─── EGIS Debug Tab ──────────────────────────────────────────────────────────

interface EgisTestAttempt {
  contentType: string
  httpStatus: number
  ok: boolean
  responseBody: string
}

interface EgisDebugResult {
  success: boolean
  baseUrl: string
  operationUrl?: string
  login: string
  erpName: string
  httpStatus: number
  xmlSent: string
  responseBody: string
  attempts: EgisTestAttempt[]
}

function EgisDebugTab() {
  const [query, setQuery] = useState('hp')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<EgisDebugResult | null>(null)
  const [copied, setCopied] = useState(false)

  const run = async () => {
    setRunning(true)
    setResult(null)
    try {
      const { data } = await apiClient.get<EgisDebugResult>(`/egis/test?q=${encodeURIComponent(query)}`)
      setResult(data)
    } catch (err) {
      setResult({
        success: false,
        baseUrl: '',
        login: '',
        erpName: '',
        httpStatus: 0,
        xmlSent: '',
        responseBody: extractErrorMessage(err) || 'Anfrage fehlgeschlagen',
        attempts: [],
      })
    } finally {
      setRunning(false)
    }
  }

  const copyForSupport = () => {
    if (!result) return
    const text = [
      '=== EGIS Debug Report ===',
      `URL: ${result.operationUrl ?? result.baseUrl + '/searchQuery'}`,
      `Login: ${result.login}`,
      `ERP-Name: ${result.erpName}`,
      '',
      '--- Gesendetes XML ---',
      result.xmlSent,
      '',
      '--- Server-Antworten ---',
      ...(result.attempts.length > 0
        ? result.attempts.map(a => `[${a.contentType}] HTTP ${a.httpStatus}:\n${a.responseBody}`)
        : [result.responseBody]),
    ].join('\n')
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card>
      <Card.Header>
        <h3 className="text-h3 font-semibold flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-brand-500" />
          EGIS Business Connector – Debug
        </h3>
        <p className="text-caption text-gray-500 mt-1">
          Sendet eine direkte XML-Anfrage an den EGIS-Server und zeigt XML, HTTP-Status und Roh-Response.
          Alle drei Content-Type-Varianten werden getestet.
        </p>
      </Card.Header>
      <Card.Body className="space-y-4">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Input
              label="Suchbegriff"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="z.B. hp, lenovo, monitor..."
              onKeyDown={(e) => e.key === 'Enter' && run()}
            />
          </div>
          <Button onClick={run} loading={running} leadingIcon={<Search className="w-4 h-4" />}>
            Testen
          </Button>
          {result && (
            <Button
              variant="secondary"
              onClick={copyForSupport}
              leadingIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            >
              {copied ? 'Kopiert!' : 'Für Support kopieren'}
            </Button>
          )}
        </div>

        {result && (
          <div className="space-y-4">
            {/* Status-Uebersicht */}
            <div className={`rounded-lg border p-3 ${result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center gap-2 font-medium text-sm mb-2">
                {result.success
                  ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                  : <AlertTriangle className="w-4 h-4 text-red-600" />}
                {result.success ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen'}
              </div>
              {result.login && (
                <div className="text-caption text-gray-600 space-y-0.5">
                  <div>URL: <code className="bg-white px-1 rounded">{result.operationUrl ?? `${result.baseUrl}/searchQuery`}</code></div>
                  <div>Login: <code className="bg-white px-1 rounded">{result.login}</code> · ERP: <code className="bg-white px-1 rounded">{result.erpName}</code></div>
                </div>
              )}
            </div>

            {/* Content-Type Versuche */}
            {result.attempts.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Content-Type-Versuche</div>
                <div className="space-y-1">
                  {result.attempts.map((a) => (
                    <div key={a.contentType} className={`flex items-center gap-3 px-3 py-2 rounded border text-caption ${a.ok ? 'border-green-200 bg-green-50' : 'border-red-100 bg-red-50'}`}>
                      <StatusBadge status={a.ok ? 'active' : 'error'} label={`HTTP ${a.httpStatus}`} />
                      <code className="text-gray-600">{a.contentType}</code>
                      {!a.ok && <span className="text-red-600 truncate flex-1">{a.responseBody.slice(0, 120)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gesendetes XML */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Gesendetes XML (Passwort maskiert)</div>
              <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap font-mono">{result.xmlSent}</pre>
            </div>

            {/* Server-Antwort */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Server-Antwort</div>
              <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-64 whitespace-pre-wrap font-mono">{result.responseBody}</pre>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── SAP-Rohdaten Tab ──────────────────────────────────────────────────────────

function SapRawDataTab() {
  const [bpId, setBpId] = useState('')
  const [submitted, setSubmitted] = useState('')

  const { data, isFetching, error } = useQuery({
    queryKey: ['debug-sap-raw', submitted],
    queryFn: () => apiClient.get(`/business-partners/${submitted}`).then((r) => r.data),
    enabled: !!submitted,
  })

  const rawJson = data?.sapRawJson as Record<string, unknown> | null | undefined

  const fields = rawJson
    ? Object.entries(rawJson)
        .filter(([, v]) => v !== null && v !== '' && v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
    : []

  return (
    <Card>
      <Card.Header>
        <Card.Title>SAP Rohdaten (sapRawJson)</Card.Title>
      </Card.Header>
      <Card.Body className="space-y-4">
        <p className="text-sm text-neutral-500">
          Gibt alle non-null Felder aus dem SAP-Response eines Geschäftspartners aus.
          Nützlich um zu prüfen welche Felder SAP liefert (z.B. <code className="bg-neutral-100 px-1 rounded">RegistrationNumber</code>).
        </p>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
            value={bpId}
            onChange={(e) => setBpId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSubmitted(bpId.trim())}
            placeholder="Business Partner UUID oder SAP CardCode..."
          />
          <Button variant="primary" size="sm" onClick={() => setSubmitted(bpId.trim())} disabled={isFetching || !bpId.trim()}>
            {isFetching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Laden
          </Button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {extractErrorMessage(error)}
          </div>
        )}

        {rawJson === null && submitted && !isFetching && (
          <p className="text-sm text-neutral-500">Kein sapRawJson vorhanden – GP wurde nie aus SAP synchronisiert.</p>
        )}

        {fields.length > 0 && (
          <div className="divide-y divide-neutral-100 border border-neutral-200 rounded-md overflow-hidden text-sm">
            {fields.map(([key, value]) => (
              <div key={key} className={`flex gap-4 px-4 py-2 ${key === 'RegistrationNumber' ? 'bg-yellow-50' : 'bg-white'}`}>
                <span className="font-mono text-xs text-neutral-500 w-64 shrink-0 pt-0.5">{key}</span>
                <span className="font-mono text-xs break-all text-neutral-800">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        )}

        {fields.length === 0 && rawJson && !isFetching && (
          <p className="text-sm text-neutral-500">sapRawJson ist leer (alle Felder null/leer).</p>
        )}
      </Card.Body>
    </Card>
  )
}

const VALID_TABS = ['sync-ops', 'adapters', 'audit', 'sap-doc-cache', 'tanss', 'sync-map', 'job-history', 'self-test', 'egis', 'sap-raw'] as const
type TabId = typeof VALID_TABS[number]

export function DevToolsPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const tabFromUrl = new URLSearchParams(location.search).get('tab') as TabId | null
  const tab: TabId = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'sync-ops'

  function setTab(newTab: string) {
    navigate(`/debug?tab=${newTab}`, { replace: true })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-brand-600" />
            System
          </span>
        }
        description="Integrationen, Sync-Vorgänge, Adapter-Tests, Audit-Logs und Diagnose-Werkzeuge"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="sync-ops">Sync-Vorgänge</TabsTrigger>
          <TabsTrigger value="adapters">Adapter-Tests</TabsTrigger>
          <TabsTrigger value="audit">Audit-Logs</TabsTrigger>
          <TabsTrigger value="sap-doc-cache">SAP-Dokument-Cache</TabsTrigger>
          <TabsTrigger value="sync-map">Sync-Map</TabsTrigger>
          <TabsTrigger value="tanss">TANSS-API</TabsTrigger>
          <TabsTrigger value="job-history">Job-History</TabsTrigger>
          <TabsTrigger value="self-test">Self-Test</TabsTrigger>
          <TabsTrigger value="egis">EGIS-Debug</TabsTrigger>
          <TabsTrigger value="sap-raw">SAP-Rohdaten</TabsTrigger>
        </TabsList>
        <TabsContent value="sync-ops">
          <ScheduledJobsTab />
        </TabsContent>
        <TabsContent value="adapters">
          <AdapterTestsTab />
        </TabsContent>
        <TabsContent value="audit">
          <AuditLogsTab />
        </TabsContent>
        <TabsContent value="sap-doc-cache">
          <SapDocCacheTab />
        </TabsContent>
        <TabsContent value="sync-map">
          <SyncMapTab />
        </TabsContent>
        <TabsContent value="tanss">
          <TanssApiViewerTab />
        </TabsContent>
        <TabsContent value="job-history">
          <JobHistoryTab />
        </TabsContent>
        <TabsContent value="self-test">
          <SelfTestTab />
        </TabsContent>
        <TabsContent value="egis">
          <EgisDebugTab />
        </TabsContent>
        <TabsContent value="sap-raw">
          <SapRawDataTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default DevToolsPage
